import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import AuctionDraftBuilder from './AuctionDraftBuilder';
import { getCachedRosters, fetchCurrentRosters, applyRosterUpdates } from './utils/rosterUpdate';
import { UNIQUE_PLAYERS } from './data/players';
import { PlayerCardProvider } from './context/PlayerCardContext';
import PlayerCard from './components/PlayerCard';
import ScoutPage from './components/Scout/ScoutPage';
import WatchPage, { SAMPLE_SHARE_ID } from './components/LiveBoard/WatchPage';
import SubmitPage from './components/Submit/SubmitPage';
import RatePage from './components/Rate/RatePage';
import { initAnalytics } from './utils/analyticsGate';
import DevDialsBadge from './components/DevDialsBadge';

// Gated: no init on localhost/dev builds, or on a device flagged internal via
// ?internal=1 (cleared with ?internal=0). See utils/analyticsGate.js.
initAnalytics(posthog, 'phc_AiDbFeRCMvESd4kr9t9xtgpipPwYf26WAD89aroM3Gmb', {
  api_host: 'https://us.i.posthog.com',
  person_profiles: 'identified_only',
  capture_pageview: true,
  capture_pageleave: true,
  // Session replay intentionally ON as of 2026-07-23 for user-behavior review.
  // If the recorder ever interferes with the ~80s Almanac Yahoo fetch (it was
  // previously interleaving lazy-loaded-session-recorder traffic with every
  // Yahoo call), scope it with posthog.stopSessionRecording()/
  // startSessionRecording() around that path instead of disabling globally.
  disable_session_recording: false,
  // Disable PostHog's DOM-observing autocapture. Its MutationObserver/rrweb-style
  // measurement fought dnd-kit's useRect during a drag, driving an infinite
  // measure→setState loop (React #185 crash on cross-tier tier drags). We only
  // use explicit posthog.capture() + pageviews, so the auto-observers add no
  // value here. (The .tier-pill width revert + Tier Builder ErrorBoundary stay
  // as defense-in-depth; this removes the actual cause.)
  autocapture: false,
  capture_dead_clicks: false,
  capture_heatmaps: false,
});

function App() {
  // /scout is its own standalone surface (Sleeper-only, forward intel) — kept
  // fully separate from the builder/Almanac render path. Lightweight pathname
  // routing, matching the app's existing no-react-router pattern.
  const isScout = typeof window !== "undefined" && window.location.pathname === "/scout";
  // /watch is the read-only spectator feed — one writer, many readers. Its own
  // standalone surface, deliberately OUTSIDE PlayerCardProvider exactly like
  // /scout: it renders nothing that opens a player card, and RosterBoard takes
  // an optional click handler rather than calling usePlayerCard (which
  // destructures a null context and throws with no provider above it).
  //
  // 🔴 THE PATH DECIDES THE ROUTE, NOT THE ID. A /watch with no ?b= used to
  // fall through to the builder, so a spectator sent a link that got cut off in
  // a text got the whole builder on their phone — the entire hook chain, a
  // localStorage migration and the roster fetch, on a device with no league
  // saved for any of it to read. The side effects are the reason, not the copy.
  // WatchPage owns the missing-id message; the null id must reach it, not
  // bypass it.
  //
  // 🔴 /watch/sample IS THE SAME ROUTE, NOT A SECOND ONE. The sample board is a
  // bundled fixture rendered by WatchPage under a reserved id, so a visitor who
  // has never run an auction can see what the share link actually looks like
  // before typing eleven team names. Every OTHER /watch/… path falls to
  // WatchPage with a null id — the "missing board id" message — rather than
  // becoming a second, undocumented way to address a real board.
  const watchPath = typeof window !== "undefined" ? window.location.pathname : "";
  const isWatch = watchPath === "/watch" || watchPath.startsWith("/watch/");
  const watchId = !isWatch ? null
    : watchPath === "/watch/sample" ? SAMPLE_SHARE_ID
      : new URLSearchParams(window.location.search).get("b");

  // /submit is the "add your league to the data" page — a standalone surface
  // like /scout: one input, one POST, no builder state of any kind.
  const isSubmit = typeof window !== "undefined" && window.location.pathname === "/submit";

  // /rate is the post-draft report card — same standalone-surface pattern as
  // /submit: one input, one POST, the report renders from the response.
  const isRate = typeof window !== "undefined" && window.location.pathname === "/rate";

  useEffect(() => {
    if (isScout || isWatch || isSubmit || isRate) return; // none uses the builder's local roster DB
    const cached = getCachedRosters();
    if (cached) {
      applyRosterUpdates(UNIQUE_PLAYERS, cached.data);
    } else {
      fetchCurrentRosters()
        .then(updates => applyRosterUpdates(UNIQUE_PLAYERS, updates))
        .catch(() => {});
    }
  }, [isScout, isWatch, isSubmit, isRate]);

  if (isWatch) {
    return (
      <PostHogProvider client={posthog}>
        <WatchPage shareId={watchId} />
        <Analytics />
        <SpeedInsights />
      </PostHogProvider>
    );
  }

  if (isSubmit) {
    return (
      <PostHogProvider client={posthog}>
        <SubmitPage />
        <Analytics />
        <SpeedInsights />
      </PostHogProvider>
    );
  }

  if (isRate) {
    return (
      <PostHogProvider client={posthog}>
        <RatePage />
        <Analytics />
        <SpeedInsights />
      </PostHogProvider>
    );
  }

  if (isScout) {
    return (
      <PostHogProvider client={posthog}>
        <ScoutPage />
        <Analytics />
        <SpeedInsights />
        <DevDialsBadge />
      </PostHogProvider>
    );
  }

  return (
    <PostHogProvider client={posthog}>
      <PlayerCardProvider>
        <AuctionDraftBuilder />
        <PlayerCard />
        <Analytics />
        <SpeedInsights />
        <DevDialsBadge />
      </PlayerCardProvider>
    </PostHogProvider>
  );
}

export default App;
